<template>
    <AxRoadPage title="Fahrtenverlauf" class="trip-history">
        <DateSelect
            :value="selectedDate"
            class="trip-history__date"
            @input="handleDateSelect"
        />
        <div class="trip-history__title">
            {{ formatDistance(totalDistance) }} {{ $t('total') }}
            <AxRoadButton @click="handleExpandButtonClick">
                <ExpandMultiIcon width="16" height="16" />
                <span>{{ $t('detailedTrips') }}</span>
            </AxRoadButton>
        </div>
        <div class="l-inline l-gap-2">
            <div class="trip-history__map-container">
                <div v-if="$refs.map" class="trip-history__map-controls">
                    <MapControls
                        :disabled-controls="{
                            displayOptions: true,
                            labelOptions: true,
                            search: true,
                            userLocation: true,
                            zoom: true,
                        }"
                        :map="$refs.map.mapObject"
                    />
                </div>

                <LMap
                    ref="map"
                    class="trip-history__map"
                    :options="{
                        attributionControl: false,
                        preferCanvas: true,
                        zoomControl: false,
                    }"
                    :max-bounds="maxBounds"
                    :max-bounds-viscosity="maxBoundsViscosity"
                    :max-zoom="activeTileProvider.maxZoom"
                    :min-zoom="activeTileProvider.minZoom"
                >
                    <template v-if="isRadialSearch">
                        <LocationLayer
                            v-if="activeLocationOnMap"
                            :locations="[activeLocationOnMap]"
                            @alignMap="alignMap"
                        />
                        <RadialSearchLayer
                            v-if="canPerformRadialSearch"
                            @alignMap="alignMap"
                        />
                    </template>

                    <TripHistoryLayer :map-zoom="zoom" @alignMap="alignMap" />

                    <HistoryPointLayer />
                </LMap>
                <div v-if="isLoadingTrips" class="trip-history__spinner">
                    <VSpinner
                        size="medium"
                        :line-fg-color="spinnerColor"
                        :speed="1"
                    />
                </div>
            </div>
            <div v-if="isTripsListVisible" class="trip-history__list">
                <div v-for="(trip, index) in trips" :key="trip.id">
                    <TripListItem
                        :trip="trip"
                        :class="'historyItem-' + trip.id"
                        @click="handleListItemClick"
                    />

                    <TripPauseListItem
                        v-if="trips[index + 1]"
                        :trip-from="trip"
                        :trip-to="trips[index + 1]"
                    />
                </div>
            </div>
        </div>
        <div class="trip-history__chart-container">
            <AssetLocationHistoryMeasurementsChart
                v-if="selectedMeasurement"
                :id="id"
                :measurement="selectedMeasurement"
                :history-points="chartHistoryPoints"
                :is-loading="isLoadingTrips"
                @pointChange="handleHistoryPointChange"
                @boundsChange="handleBoundsChange"
            />
            <BaseMultiselect
                v-model="selectedMeasurement"
                :options="availableMeasurements"
                :custom-label="
                    option =>
                        $root.$te(`shared.measurements.${option}`)
                            ? $t(`shared.measurements.${option}`)
                            : option
                "
                @tag="selectedMeasurement = $event"
            />
        </div>
    </AxRoadPage>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import moment from 'moment'
import { debounce } from 'lodash'
import { LMap } from 'vue2-leaflet'
import VSpinner from 'vue-simple-spinner'

import AssetLocationHistoryMeasurementsChart from '@/components/AssetLocationHistoryMeasurementsChart'
import AxRoadButton from '@/components/ax-road/AxRoadButton'
import AxRoadPage from '@/components/ax-road/AxRoadPage'
import BaseMultiselect from '@/components/redesigned/BaseMultiselect'
import DateSelect from '@/components/ax-road/DateSelect'
import ExpandMultiIcon from '@/components/icons/ExpandMultiIcon'
import HistoryPointLayer from '@/components/HistoryPointLayer'
import LocationLayer from '@/components/LocationLayer'
import MapControls from '@/components/redesigned/MapControls'
import MapHelper from '@/mixins/MapHelper'
import RadialSearchLayer from '@/components/RadialSearchLayer'
import TripHistoryLayer from '@/components/TripHistoryLayer'
import TripListItem from '@/components/TripListItem'
import TripPauseListItem from '@/components/TripPauseListItem'

export default {
    name: 'TripHistoryView',
    components: {
        AssetLocationHistoryMeasurementsChart,
        AxRoadButton,
        AxRoadPage,
        BaseMultiselect,
        DateSelect,
        ExpandMultiIcon,
        HistoryPointLayer,
        LMap,
        LocationLayer,
        MapControls,
        RadialSearchLayer,
        TripHistoryLayer,
        TripListItem,
        TripPauseListItem,
        VSpinner,
    },
    mixins: [MapHelper],
    props: {
        truckId: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            selectedDate: moment().format('YYYY-MM-DD'), // Default to today
            spinnerColor: process.env.VUE_APP_COLOR_PRIMARY,
            selectedMeasurement: null,
            zoom: null,
            isTripsListVisible: false,
        }
    },
    computed: {
        ...mapState('trip', ['trips', 'isLoadingTrips']),
        ...mapState('map', ['forceAlign']),
        ...mapState('location', ['activeLocationOnMap']),
        ...mapGetters('tracker', ['assetsById']),
        ...mapGetters('trip', ['totalDistance', 'selectedTrip']),
        ...mapGetters('map', ['activeTileProvider']),
        ...mapGetters('axroad', ['canPerformRadialSearch']),
        truckDetails() {
            return this.assetsById[this.id]
        },
        availableMeasurements() {
            return Object.keys(
                this.truckDetails?.asset_details?.sensor_data ?? []
            ).sort()
        },
        chartHistoryPoints() {
            if (!this.trips?.length) {
                return []
            }

            if (this.selectedTrip) {
                return this.selectedTrip.measurements.map(measurement => ({
                    timestamp: measurement.timestamp,
                    position: [measurement.lat, measurement.lng],
                }))
            }

            return this.trips.reduce((acc, cur) => {
                return [
                    ...acc,
                    ...cur.measurements.map(measurement => ({
                        timestamp: measurement.timestamp,
                        position: [measurement.lat, measurement.lng],
                    })),
                ]
            }, [])
        },
        id() {
            return (
                this.truckId ??
                this.$route.params.assetId ??
                this.$route.params.id
            )
        },
        isRadialSearch() {
            return this.$route.name === 'axRoadSearchResultDetails'
        },
    },
    watch: {
        '$route.query.date': {
            handler() {
                this.selectedDate =
                    this.$route.query.date || moment().format('YYYY-MM-DD')
                this.fetchTrips()
            },
            immediate: true,
        },
        id() {
            this.fetchTrips()
        },
    },
    mounted() {
        this.selectedMeasurement = this.availableMeasurements.includes('speed')
            ? 'speed'
            : this.availableMeasurements[0]
    },
    methods: {
        ...mapActions('trip', ['loadTrips']),
        ...mapMutations('tracker', ['setAssetHistoryPoint']),
        ...mapMutations('trip', ['setSelectedTripId']),
        fetchTrips() {
            if (!this.truckDetails?.asset_details?.id) {
                return
            }
            const fromDate = `${this.selectedDate}T00:00:00Z`
            const toDate = `${this.selectedDate}T23:59:59Z`
            this.loadTrips({
                assetId: this.truckDetails?.asset_details?.id,
                fromDate,
                toDate,
            })
        },
        formatDistance(distance) {
            return `${(distance / 1000).toFixed(2)} ${this.$t(
                'shared.units.km'
            )}`
        },
        areCoordinatesVisible(coordinates) {
            return coordinates.every(item =>
                this.$refs.map.mapObject.getBounds().contains(item)
            )
        },
        alignMap(bounds) {
            if (
                bounds?.length &&
                (this.forceAlign ||
                    this.$refs.map.mapObject.getZoom() < 12 ||
                    !this.areCoordinatesVisible(bounds))
            ) {
                this.$nextTick(
                    debounce(() => this.$refs.map.fitBounds(bounds), 100)
                )
            }
        },
        handleDateSelect(date) {
            this.$router.replace({
                ...this.$route,
                query: { ...this.$route.query, date },
            })
        },
        handleHistoryPointChange(point) {
            if (
                !point ||
                typeof point.position[0] !== 'number' ||
                typeof point.position[1] !== 'number'
            ) {
                this.setAssetHistoryPoint(null)
                return
            }

            this.setAssetHistoryPoint(point.position)

            if (
                !this.$refs.map.mapObject.getBounds().contains(point.position)
            ) {
                this.$refs.map.mapObject.flyToBounds([
                    point.position,
                    ...this.chartHistoryPoints.map(({ position }) => position),
                ])
            }
        },
        handleBoundsChange(bounds) {
            this.$refs.map.mapObject.flyToBounds(bounds)
        },
        handleListItemClick({ id }) {
            this.setSelectedTripId(this.selectedTrip?.id === id ? null : id)
        },
        handleExpandButtonClick() {
            this.isTripsListVisible = !this.isTripsListVisible
            this.$nextTick(() => {
                this.$refs.map.mapObject.invalidateSize()
            })
        },
    },
}
</script>

<i18n>
{
  "en": {
    "total": "Total",
    "detailedTrips": "Detailed Trips"
  },
  "de": {
    "total": "Gesamt",
    "detailedTrips": "Detaillierte Fahrten"
  },
  "fr": {
    "total": "Total",
    "detailedTrips": "Trajets détaillés"
  },
  "it": {
    "total": "Totale",
    "detailedTrips": "Viaggi dettagliati"
  }
}
</i18n>

<style lang="scss">
.trip-history {
    &__date {
        margin-bottom: 16px;
    }

    &__title {
        display: flex;
        justify-content: space-between;
        font-size: 20px;
        line-height: 28px;
        font-weight: 500;
        margin-bottom: 16px;
        color: $color-text;
    }

    &__map-container {
        position: relative;
        width: 100%;
        height: 560px;
        margin-bottom: 16px;
    }

    &__map-controls {
        position: absolute;
        top: 1rem;
        right: 1rem;
        left: 1rem;
        pointer-events: none;
        z-index: 1000;
    }

    &__spinner {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
        background-color: transparentize($color-gray-dark, 0.5);
    }

    &__map {
        height: 560px;
        width: 100%;
        flex: 1;
        overflow: hidden;
        z-index: 0;
    }

    &__chart-container {
        position: relative;
        z-index: 1001;
    }

    &__list {
        background-color: $color-gray-light-2;
        width: 340px;
        height: 560px;
        border-radius: 8px;
        overflow: auto;
    }

    @include respond-to('for-mobile-down') {
        &__map-controls {
            display: none;
        }
    }
}
</style>
